<template>
  <CFormItem :prop="name" :field-value="value">
    <ElInput
      v-model="value"
      class="cell-text-edit"
      :class="{ 'show-edit-icon': editByIcon }"
      :type="params.type"
      :maxlength="params.maxlength"
      :show-word-limit="params.showWordLimit"
      :disabled="disabled || editByIcon"
      @change="valueChange"
      @focus="autoSelect($event)"
      @keydown.stop="noop"
      @click.stop="noop"
      @blur="editByIcon = params.editByIcon"
    >
      <template v-if="editByIcon && !disabled" #suffix>
        <ElIcon
          class="edit-icon"
          color="#327DE7"
          size="12px"
          @click="editByIcon = false"
        >
          <EditPen />
        </ElIcon>
      </template>
    </ElInput>
  </CFormItem>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { ElIcon } from 'element-plus';
import { EditPen } from '@element-plus/icons-vue';
import { ICellRendererParams } from 'ag-grid-community';
import { useShareData } from '../../core/SharedData';
import { noop, isFunction, isString } from 'lodash-es';
import CFormItem from './CFormItem.vue';

const props = defineProps<{
  params: ICellRendererParams<any> & {
    fieldName?: string; // 字段名

    /*编辑框是否禁用 */
    disabled?: boolean | string | ((ps: ICellRendererParams<any>) => boolean);

    // 输入文本最大长度
    maxlength?: number;

    //是否显示统计字数
    showWordLimit?: boolean;

    // 文本框类型
    type?: string;

    // textarea的行数
    //rows?: number;

    // 点击自动选中文字
    autoSelect?: boolean;

    thin?: boolean; // 比较窄的模式

    editByIcon?: boolean; // 点击icon进入编辑模式, 而不是显示编辑框

    valueChange?: (
      params: ICellRendererParams<any>,
      newValue: any,
      oldValue: string
    ) => void;
  };
}>();

// 字段名
// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
const name = props.params.fieldName || props.params.colDef?.field!;

const tableSetting = useShareData('table', 'setting');

const editByIcon = ref(props.params.editByIcon);

const disabled = computed(() => {
  // table全局禁止edit
  if (tableSetting?.disabledEdit) {
    return true;
  }

  const paramsDisabled = props.params.disabled;
  return isString(paramsDisabled)
    ? !!props.params.data[paramsDisabled]
    : isFunction(paramsDisabled)
    ? paramsDisabled(props.params)
    : paramsDisabled;
});

let value = ref(props.params.value);
// value为input的绑定值
// const value = computed({
//   get() {
//     // 如果为空
//     if (!text.value) {
//       // 禁用状态下显示-
//       return '';
//     }
//     return text.value;
//   },

//   set(v: string) {
//     text.value = v;
//   }
// });

async function valueChange(val: string) {
  const old = props.params.value;
  try {
    await props.params.valueChange?.(props.params, val, old);
    props.params.node.setDataValue(name, val, 'form'); // 'form'参数用于区分谁触发的
  } catch (e) {
    value.value = old;
    throw e;
  }
}

function autoSelect(event: any) {
  if (props.params.autoSelect) {
    event.target.select();
  }
}

// 窄模式
const margin = props.params.thin ? '0px' : '8px';
const height = props.params.thin ? '22px' : 'var(--el-input-inner-height)';
const padding = props.params.thin ? '6px' : '11px';
</script>

<style lang="scss">
.cell-text-edit {
  width: calc(100% - v-bind(margin) * 2) !important;
  box-sizing: border-box;
  margin-left: v-bind(margin);
  margin-right: v-bind(margin);
  vertical-align: middle !important;

  &.show-edit-icon,
  &.is-disabled:hover,
  &.is-disabled {
    cursor: default !important;

    .el-input__wrapper {
      --el-disabled-bg-color: transparent;
      --el-disabled-text-color: var(--el-input-text-color);

      border-color: transparent !important;
      background: transparent !important;
      box-shadow: none !important;

      input {
        cursor: default;
      }
    }

    textarea,
    textarea:hover,
    textarea:focus {
      box-shadow: none !important;
      background-color: transparent !important;
      resize: none;
      cursor: default !important;
    }
  }

  .el-input__wrapper {
    border: 1px solid #dcdfe655;
    box-shadow: none;
    padding: 1px v-bind(padding);
  }

  .el-input__wrapper:hover,
  .el-input__wrapper.is-focus {
    border-color: #dcdfe6;
    box-shadow: none;
  }

  .el-input__inner {
    height: v-bind(height);
  }

  textarea {
    height: 36px;
    box-shadow: 0 0 0 1px #dcdfe655 inset !important;
  }

  textarea:hover,
  textarea:focus {
    box-shadow: 0 0 0 1px #dcdfe6 inset !important;
  }

  .edit-icon {
    cursor: pointer;
  }
}
</style>
